import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { AGE_GROUPS } from "@/lib/tracks";
import { useKioskIdleReturn } from "@/lib/useKioskIdleReturn";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";

// Public "self sign-up" page reached by scanning the tracker QR code.
// Anyone (with the URL) can add themselves as a guest at the org — no auth,
// no service window check. Coord/teacher hands their phone or a printed QR.
export default function TrackerSignup() {
  useKioskIdleReturn();
  const { code } = useParams();
  const [orgName, setOrgName] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState({
    first_name: "", last_name: "", gender: null, phone: "", email: "",
    address_city: "", age_group: "", referral_source: "QR signup", invited_by: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/tracker/${code}`)
      .then((r) => setOrgName(r.data.organization?.name || ""))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "This QR isn\u2019t valid."));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.first_name.trim() || form.last_name.trim())) {
      toast.error("Please enter your name.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/public/tracker/${code}/new-guest`, form);
      setDone(r.data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setForm({ first_name: "", last_name: "", gender: null, phone: "", email: "", address_city: "", age_group: "", referral_source: "QR signup", invited_by: "", notes: "" });
    setDone(null);
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg-base)" }}>
        <div className="max-w-sm text-center" data-testid="tracker-signup-invalid">
          <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>QR not recognized</h2>
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>{loadErr}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "var(--bg-base)" }}>
        <div className="w-full max-w-md text-center p-8 card-surface" style={{ background: "var(--bg-alt)" }} data-testid="tracker-signup-success">
          <div className="w-14 h-14 rounded-full inline-flex items-center justify-center mb-4" style={{ background: "var(--olive)", color: "#FFF" }}>
            <CheckCircle2 size={28} />
          </div>
          <h2 className="font-serif-display text-3xl mb-2" style={{ color: "var(--ink)" }}>You&rsquo;re signed up</h2>
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>
            <strong>{formatDisplayName(done.guest)}</strong> &mdash; welcome to {done.organization?.name || orgName}.
          </p>
          <p className="text-xs mt-6 max-w-xs mx-auto" style={{ color: "var(--muted)" }}>
            A member of our team will be in touch soon. So glad you&rsquo;re here.
          </p>
          <button onClick={reset} className="btn-ghost mt-6 text-xs" data-testid="tracker-signup-again">Sign someone else up</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center px-4 py-10" style={{ background: "var(--bg-base)" }}>
      <form onSubmit={submit} className="w-full max-w-md card-surface p-6 sm:p-8" style={{ background: "var(--bg-alt)" }} data-testid="tracker-signup-form">
        <div className="overline mb-1" style={{ color: "var(--ember)", fontSize: "0.6rem" }}>Welcome{orgName ? ` to ${orgName}` : ""}</div>
        <h1 className="font-serif-display text-3xl mb-1" style={{ color: "var(--ink)" }}>Nice to meet you.</h1>
        <p className="text-xs mb-6" style={{ color: "var(--muted)" }}>
          Fill this out so we can follow up with you personally. Only your name is required.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-1">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>First name *</span>
            <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-first_name" />
          </label>
          <label className="block col-span-1">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Last name</span>
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-last_name" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Phone</span>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-phone" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-email" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>City / area</span>
            <input value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-city" />
          </label>
          <div className="col-span-2">
            <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="tracker-signup-gender" />
          </div>
          <label className="block col-span-2">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Age group</span>
            <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-age_group">
              <option value="">&mdash;</option>
              {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="block col-span-2">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Who invited you?</span>
            <input value={form.invited_by} onChange={(e) => setForm({ ...form, invited_by: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-invited_by" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Anything you&rsquo;d like us to know</span>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="tracker-signup-notes" />
          </label>
        </div>
        <button type="submit" disabled={busy} className="btn-primary w-full mt-6" data-testid="tracker-signup-submit">
          {busy ? "Signing you up\u2026" : "Sign up"}
        </button>
      </form>
    </div>
  );
}
